import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  activeTasks,
  emptyTasks,
  getTaskEntry,
  getTasksDoc,
  setTaskValue,
} from '../../db/tasks';
import { useAsync } from '../hooks/useAsync';
import { useDataVersion } from '../../db/events';
import type { TaskEntryRecord } from '../../db/schema';
import type { TaskDef, TaskKind, TasksDoc } from '../../domain/types';

/** 数値・テキストは入力欄から離れた時に保存する。チェックは即時。 */
function TaskRow({
  task,
  value,
  onChange,
}: {
  task: TaskDef;
  value: number | boolean | string | undefined;
  onChange: (v: number | boolean | string | undefined) => void;
}) {
  const [draft, setDraft] = useState(value === undefined ? '' : String(value));
  useEffect(() => {
    setDraft(value === undefined ? '' : String(value));
  }, [value]);

  if (task.type === 'check') {
    return (
      <label className="task-row">
        <input
          type="checkbox"
          checked={value === true}
          onChange={(e) => onChange(e.target.checked ? true : undefined)}
        />
        <span className={`task-name${value === true ? ' is-done' : ''}`}>{task.name}</span>
      </label>
    );
  }

  if (task.type === 'number') {
    return (
      <label className="task-row">
        <span className="task-name">{task.name}</span>
        <span className="task-input">
          <input
            type="number"
            inputMode="decimal"
            step="any"
            value={draft}
            placeholder={task.target != null ? `目標 ${task.target}` : ''}
            onChange={(e) => setDraft(e.target.value)}
            onBlur={() => {
              const t = draft.trim();
              const n = t === '' ? undefined : Number(t);
              onChange(n !== undefined && Number.isFinite(n) ? n : undefined);
            }}
          />
          {task.unit && <span className="task-unit">{task.unit}</span>}
        </span>
      </label>
    );
  }

  return (
    <label className="task-row task-row-text">
      <span className="task-name">{task.name}</span>
      <input
        type="text"
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        onBlur={() => onChange(draft.trim() === '' ? undefined : draft)}
      />
    </label>
  );
}

/**
 * その日／その週のタスク入力欄。
 * 定義から毎回組み立てるだけで、空のレコードは作らない。
 */
export function TaskChecklist({ kind, period }: { kind: TaskKind; period: string }) {
  const version = useDataVersion();
  const doc = useAsync<TasksDoc>(getTasksDoc, [version], emptyTasks());
  const entry = useAsync<TaskEntryRecord | undefined>(
    () => getTaskEntry(kind, period),
    [kind, period, version],
    undefined,
  );

  const tasks = activeTasks(doc, kind);
  if (tasks.length === 0) return null;

  const done = tasks.filter((t) => {
    const v = entry?.values[t.id];
    return v !== undefined && v !== false && v !== '';
  }).length;

  return (
    <div className="task-card">
      <div className="task-head">
        <h2>{kind === 'daily' ? 'デイリータスク' : 'ウィークリータスク'}</h2>
        <span className="muted">
          {done} / {tasks.length}
        </span>
        <Link className="icon-btn" to="/reports" title="レポート">
          ▤
        </Link>
      </div>
      <div className="task-list">
        {tasks.map((t) => (
          <TaskRow
            key={t.id}
            task={t}
            value={entry?.values[t.id]}
            onChange={(v) => setTaskValue(kind, period, t.id, v)}
          />
        ))}
      </div>
    </div>
  );
}
